import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface SEOProps {
    title: string;
    description: string;
    canonical?: string;
    noindex?: boolean;
    type?: 'website' | 'article' | 'webapplication';
    image?: string;
    schema?: Record<string, unknown>;
    datePublished?: string;
    dateModified?: string;
    author?: string;
    authorType?: 'Person' | 'Organization';
    authorUrl?: string;
}

const SEO: React.FC<SEOProps> = ({
    title,
    description,
    canonical,
    noindex = false,
    type = 'website',
    image = 'https://images.unsplash.com/photo-1504052434569-70ad5836ab65?auto=format&fit=crop&q=80&w=1200',
    schema,
    datePublished,
    dateModified,
    author,
    authorType,
    authorUrl
}) => {
    const siteTitle = 'BirthVerse';
    const location = useLocation();
    const fullTitle = `${title} | ${siteTitle}`;
    const pathname = new URL(canonical || location.pathname, 'https://birthverse.com').pathname;
    const normalizedPath = pathname.replace(/\/+$/, '') || '/';
    const canonicalPath = normalizedPath === '/' || normalizedPath.startsWith('/birthday-meaning/')
        ? normalizedPath
        : `${normalizedPath}/`;
    const currentUrl = `https://birthverse.com${canonicalPath}`;

    // Generate JSON-LD based on type
    let jsonLd = schema;

    if (!jsonLd) {
        if (type === 'article') {
            const authorName = author || 'BirthVerse Team';
            const isBirthVerseAuthor = authorName === 'BirthVerse' || authorName === 'BirthVerse Team';
            jsonLd = {
                "@context": "https://schema.org",
                "@type": "Article",
                "headline": title,
                "url": currentUrl,
                "image": [image],
                "datePublished": datePublished,
                "dateModified": dateModified,
                "author": [{
                    "@type": isBirthVerseAuthor ? 'Organization' : authorType || 'Person',
                    "name": authorName,
                    "url": authorUrl || (isBirthVerseAuthor ? 'https://birthverse.com/about/' : undefined)
                }],
                "publisher": { "@id": "https://birthverse.com/#organization" },
                "description": description
            };
        } else if (type === 'webapplication') {
            jsonLd = {
                "@context": "https://schema.org",
                "@type": "WebApplication",
                "name": title,
                "description": description,
                "url": currentUrl,
                "applicationCategory": "LifestyleApplication",
                "operatingSystem": "Any",
                "offers": {
                    "@type": "Offer",
                    "price": "0",
                    "priceCurrency": "USD"
                }
            };
        } else {
            // Default Organization/WebSite schema for home/other pages
            jsonLd = {
                "@context": "https://schema.org",
                "@type": "WebSite",
                "name": "BirthVerse",
                "url": "https://birthverse.com"
            };
        }
    }

    return (
        <Helmet defer={false}>
            {/* Standard metadata */}
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            {noindex && <meta name="robots" content="noindex,follow" />}
            <link rel="canonical" href={currentUrl} />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type === 'webapplication' ? 'website' : type} />
            <meta property="og:url" content={currentUrl} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image} />

            {/* Twitter */}
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:url" content={currentUrl} />
            <meta property="twitter:title" content={fullTitle} />
            <meta property="twitter:description" content={description} />
            <meta property="twitter:image" content={image} />

            {/* JSON-LD Schema */}
            {jsonLd && (
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            )}
        </Helmet>
    );
};

export default SEO;
